import { useEffect, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, CheckCircle, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

let _stripePromise = null;
async function getStripe() {
  if (_stripePromise) return _stripePromise;
  const { data } = await api.get("/billing/config");
  _stripePromise = loadStripe(data.publishable_key);
  return _stripePromise;
}

const APPEARANCE = {
  theme: "flat",
  variables: {
    colorPrimary: "#3A8FA5",
    colorBackground: "#FFFFFF",
    colorText: "#1E3A5F",
    colorTextSecondary: "#5F758F",
    colorDanger: "#B9502C",
    fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
    borderRadius: "6px",
    spacingUnit: "4px",
  },
  rules: {
    ".Label": { color: "#5F758F", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: "500" },
    ".Input": { border: "1px solid #E1E9F0", backgroundColor: "#FFFFFF", padding: "10px 12px" },
    ".Input:focus": { borderColor: "#3A8FA5", boxShadow: "0 0 0 1px #3A8FA5" },
    ".Tab": { border: "1px solid #E1E9F0", backgroundColor: "#F4F7FB" },
    ".Tab--selected": { borderColor: "#3A8FA5", backgroundColor: "#F9FBFA" },
  },
};

function UpdateForm({ onSuccess, isChange }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [terms, setTerms] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErrorMsg(null);
    // 1) Confirm the SetupIntent to tokenize + 3DS the new card
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: window.location.origin + "/settings?tab=billing" },
    });
    if (error) {
      setErrorMsg(error.message || "Impossible d'enregistrer la carte");
      setBusy(false);
      return;
    }
    if (!setupIntent || setupIntent.status !== "succeeded" || !setupIntent.payment_method) {
      setErrorMsg("Enregistrement de la carte en cours de vérification. Réessayez dans un instant.");
      setBusy(false);
      return;
    }
    // 2) Attach + set default + retry open invoice server-side
    try {
      const { data } = await api.post("/billing/update-payment-method", {
        payment_method_id: setupIntent.payment_method,
      });
      if (data.invoice_status === "paid") {
        toast.success("Paiement régularisé — accès réactivé");
      } else {
        toast.success(isChange ? "Carte mise à jour" : "Moyen de paiement mis à jour");
      }
      onSuccess?.();
    } catch (err) {
      log.error("update payment method", err);
      setErrorMsg(err?.response?.data?.detail || (isChange ? "Enregistrement impossible" : "Régularisation impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="border border-[#E1E9F0] rounded-md p-4 bg-[#F4F7FB]/50 flex items-start gap-3">
        <ShieldCheck size={22} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium text-[#1E3A5F]">Nouvelle carte bancaire</div>
          <div className="text-[#5F758F]">
            {isChange
              ? "Elle remplacera votre carte actuelle et sera utilisée pour tous les prochains prélèvements."
              : "Elle remplacera votre moyen de paiement actuel et sera utilisée pour régler la facture en attente."}
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Adresse de facturation</div>
        <AddressElement options={{ mode: "billing", display: { name: "organization" }, fields: { phone: "auto" } }} />
      </div>

      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Carte bancaire</div>
        <PaymentElement options={{ layout: "tabs", wallets: { applePay: "auto", googlePay: "auto", link: "never" } }} />
      </div>
      {errorMsg && (
        <div className="text-sm text-[#B9502C] bg-[#FBE8E1] border border-[#B9502C]/20 rounded p-2" data-testid="update-pm-error">{errorMsg}</div>
      )}
      <label className="flex items-start gap-2.5 text-xs text-[#5F758F] leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[#C7DEE6] text-[#3A8FA5] focus:ring-[#3A8FA5]"
          data-testid="update-pm-terms"
        />
        <span>
          J'accepte les{" "}
          <a href="/cgv" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGV</a>,{" "}
          les{" "}
          <a href="/cgu" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGU</a>{" "}
          et la{" "}
          <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">politique de confidentialité</a>{" "}
          de Questiomed.
        </span>
      </label>
      <Button
        type="submit"
        disabled={!stripe || busy || !terms}
        className="w-full rounded-md bg-[#3A8FA5] hover:bg-[#164E5F] text-white h-11 transition-colors disabled:opacity-50"
        data-testid="update-pm-submit"
      >
        <Lock size={16} className="mr-2" />
        {busy
          ? (isChange ? "Enregistrement…" : "Régularisation…")
          : (isChange ? "Enregistrer" : "Mettre à jour et régler")}
      </Button>
      <p className="text-xs text-[#5F758F] text-center">
        Paiement sécurisé — vos coordonnées bancaires ne transitent jamais par Questiomed.
      </p>
    </form>
  );
}

export default function UpdatePaymentMethodDialog({ open, onOpenChange, onCompleted, context = "regularize" }) {
  const [stripe, setStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | success | error
  const isChange = context === "change";

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setState("loading");
      return;
    }
    (async () => {
      try {
        setState("loading");
        setStripe(await getStripe());
        const { data } = await api.post("/billing/setup-intent", {});
        setClientSecret(data.client_secret);
        setState("ready");
      } catch (err) {
        log.error("setup intent init", err);
        toast.error(err?.response?.data?.detail || "Impossible d'initialiser la régularisation");
        setState("error");
        onOpenChange(false);
      }
    })();
  }, [open, onOpenChange]);

  const handleSuccess = useCallback(() => {
    setState("success");
    onCompleted?.();
    setTimeout(() => onOpenChange(false), 1400);
  }, [onCompleted, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="update-pm-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F]">
            {isChange ? "Modifier ma carte" : "Régulariser le paiement"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5F758F]">
            {isChange
              ? "Enregistrez une nouvelle carte, elle sera utilisée pour les prochains prélèvements mensuels."
              : "Mettez à jour votre moyen de paiement pour réactiver immédiatement votre accès."}
          </DialogDescription>
        </DialogHeader>

        {state === "loading" && (
          <div className="flex items-center justify-center py-12">
            <div className="flex gap-2">
              <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot"></span>
              <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.15s" }}></span>
              <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.3s" }}></span>
            </div>
          </div>
        )}

        {state === "success" && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle size={48} weight="duotone" className="text-[#3A8FA5] mb-3" />
            <p className="text-xl font-serif text-[#3A8FA5]">{isChange ? "Carte mise à jour" : "Accès réactivé"}</p>
            <p className="text-sm text-[#5F758F] mt-2">
              {isChange
                ? "Elle sera utilisée pour vos prochains prélèvements."
                : "Merci — votre abonnement est de nouveau actif."}
            </p>
          </div>
        )}

        {state === "ready" && stripe && clientSecret && (
          <Elements stripe={stripe} options={{ clientSecret, appearance: APPEARANCE }}>
            <UpdateForm onSuccess={handleSuccess} isChange={isChange} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}
